import type { ReviewReason } from '@siliconx/shared/inventory/hardware-ingest-schema';

/**
 * SIL-1205. Turns the machine-readable reason an asset is sitting in Customs
 * (v_asset_list.customs_review_reason, migration 941) into the one plain
 * sentence + one action the acceptance criteria call for. Mirrors
 * `REVIEW_REASON_LABEL` in `ProbeIngestFeed.tsx` (same four-value vocabulary,
 * `ReviewReason` from `hardware-ingest-schema.ts`) but as a full sentence and
 * a remediation action rather than a short inline label, since this renders
 * on the fleet list/detail rather than next to a live probe reading.
 *
 * `null` covers a Customs asset with no hardware_identity_ingest row at all
 * (not probe-created, or in Customs for a reason outside the SIL-1188
 * confidence check) — same fallback ProbeIngestFeed uses
 * (DEFAULT_REVIEW_REASON_LABEL), but routed to "Contact support" here since
 * there is no candidate-model action to offer for it.
 */
/**
 * `action` is a discriminated union rather than a bare href string so the
 * caller can route an internal target through TanStack Router's typed
 * `Link` (which needs a route literal, not an arbitrary `string`) and a
 * support request through a plain `mailto:` anchor, without either branch
 * losing type-checking.
 */
export type CustomsReasonAction =
  | { kind: 'customs-triage' }
  | { kind: 'contact-support'; mailto: string };

export interface CustomsReasonCopy {
  sentence: string;
  actionLabel: string;
  action: CustomsReasonAction;
}

const SUPPORT_MAILTO = (assetId: string) =>
  `mailto:support@siliconx.com?subject=${encodeURIComponent(
    `Asset in Customs needs review — ${assetId}`,
  )}&body=${encodeURIComponent(`Asset ${assetId} is in Customs and needs manual review.`)}`;

export function customsReasonCopy(
  reason: ReviewReason | null,
  assetId: string,
): CustomsReasonCopy {
  switch (reason) {
    case 'no_candidate':
      return {
        sentence: 'No catalog match was found for this hardware.',
        actionLabel: 'Pick the matching model',
        action: { kind: 'customs-triage' },
      };
    case 'tied_candidate':
      return {
        sentence: 'Multiple catalog models matched equally well.',
        actionLabel: 'Pick the matching model',
        action: { kind: 'customs-triage' },
      };
    case 'low_overlap':
      return {
        sentence: "The closest catalog match wasn't a strong enough fit.",
        actionLabel: 'Pick the matching model',
        action: { kind: 'customs-triage' },
      };
    case 'unmatched_child':
      return {
        sentence: 'Not every component in this system matched.',
        actionLabel: 'Review components',
        action: { kind: 'customs-triage' },
      };
    default:
      return {
        sentence: 'This asset needs manual review before it can be used.',
        actionLabel: 'Contact support',
        action: { kind: 'contact-support', mailto: SUPPORT_MAILTO(assetId) },
      };
  }
}

/** Type guard: narrows the API's plain `string | null` to `ReviewReason | null`
 * — a value outside the known vocabulary (schema drift, a future value the
 * client hasn't shipped for yet) falls back to the generic sentence rather
 * than rendering something unrecognized as if it were understood. */
const KNOWN_REASONS: ReadonlySet<string> = new Set<ReviewReason>([
  'no_candidate',
  'tied_candidate',
  'low_overlap',
  'unmatched_child',
]);

export function toReviewReason(value: string | null): ReviewReason | null {
  return value != null && KNOWN_REASONS.has(value) ? (value as ReviewReason) : null;
}
